/**
 * Subscribe to the device's current audio input route label.
 *
 * Exposed as a hook so record-control UIs can show what mic is
 * actually capturing — the user's "is AirPods picking this up, or
 * am I recording through the phone in my pocket?" check. On the
 * desktop stub (dev in browser) the label is always null.
 *
 * The Swift handler returns a three-tiered fallback: active route
 * first (what's capturing right now), then available inputs (what
 * iOS could route to), then a synthetic "iPhone Microphone"
 * placeholder so the chip is never blank. Routes change dynamically
 * as the user plugs in / unplugs BT headsets — we poll every 3s
 * to approximate a route-change notification.
 */
import { useEffect, useState } from "react";
import { invoke } from "@tauri-apps/api/core";

import { usePlatform } from "./usePlatform";

const POLL_INTERVAL_MS = 3000;

export interface AudioInputRoute {
  label: string;
  portType: string;
  active: boolean;
}

export function useAudioInputRoute(
  enabled: boolean = true
): AudioInputRoute | null {
  const { isNative } = usePlatform();
  const [route, setRoute] = useState<AudioInputRoute | null>(null);

  useEffect(() => {
    if (!enabled || !isNative) {
      setRoute(null);
      return;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        const resp = await invoke<AudioInputRoute>(
          "plugin:voice|get_audio_input_route"
        );
        if (!cancelled) setRoute(resp);
      } catch {
        if (!cancelled) setRoute(null);
      }
    };

    void refresh();
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, isNative]);

  return route;
}
